/**
 * 缓存资源指令
 * 自动拦截图片和视频的加载，使用缓存
 * 自动管理 Blob URL 生命周期，防止内存泄漏
 */

import type { Directive, DirectiveBinding } from 'vue'
import { getCachedResourceUrl, isCacheableResource } from '@/utils/cached-resource'

/**
 * 存储已创建的 Blob URL，用于清理
 */
const blobUrlMap = new WeakMap<HTMLElement, string>()

/**
 * 清理 Blob URL
 */
const revokeBlobUrl = (el: HTMLElement) => {
  const blobUrl = blobUrlMap.get(el)
  if (blobUrl && blobUrl.startsWith('blob:')) {
    URL.revokeObjectURL(blobUrl)
    blobUrlMap.delete(el)
  }
}

/**
 * 处理图片元素
 */
const handleImage = async (el: HTMLImageElement, url: string) => {
  if (!isCacheableResource(url)) {
    el.src = url
    return
  }

  try {
    const cachedUrl = await getCachedResourceUrl(url)
    // 清理旧的 Blob URL
    revokeBlobUrl(el)
    // 保存新的 Blob URL
    if (cachedUrl.startsWith('blob:')) {
      blobUrlMap.set(el, cachedUrl)
    }
    el.src = cachedUrl
  } catch (error) {
    console.warn('加载缓存图片失败，使用原始 URL:', error)
    el.src = url
  }
}

/**
 * 处理视频元素
 */
const handleVideo = async (el: HTMLVideoElement, url: string) => {
  if (!isCacheableResource(url)) {
    el.src = url
    return
  }

  try {
    const cachedUrl = await getCachedResourceUrl(url)
    // 清理旧的 Blob URL
    revokeBlobUrl(el)
    // 保存新的 Blob URL
    if (cachedUrl.startsWith('blob:')) {
      blobUrlMap.set(el, cachedUrl)
    }
    el.src = cachedUrl
  } catch (error) {
    console.warn('加载缓存视频失败，使用原始 URL:', error)
    el.src = url
  }
}

/**
 * 缓存资源指令
 * 用法: v-cached-resource="imageUrl" 或 v-cached-resource
 */
export const vCachedResource: Directive = {
  mounted(el: HTMLElement, binding: DirectiveBinding<string | undefined>) {
    const url = binding.value || (el as HTMLImageElement | HTMLVideoElement).src

    if (!url) {
      return
    }

    if (el instanceof HTMLImageElement) {
      void handleImage(el, url)
    } else if (el instanceof HTMLVideoElement) {
      void handleVideo(el, url)
    }
  },

  updated(el: HTMLElement, binding: DirectiveBinding<string | undefined>) {
    const url = binding.value || (el as HTMLImageElement | HTMLVideoElement).src

    if (!url) {
      return
    }

    if (el instanceof HTMLImageElement) {
      void handleImage(el, url)
    } else if (el instanceof HTMLVideoElement) {
      void handleVideo(el, url)
    }
  },

  unmounted(el: HTMLElement) {
    // 清理 Blob URL
    revokeBlobUrl(el)
  },
}
